import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { ArrowLeft, Eye, Scan, Activity, Zap, Syringe, Car, Pill, Check, ChevronDown, ChevronUp, Info, User, Stethoscope } from 'lucide-react';

// Nuovo Listino prezzi completo - Gennaio 2026
const LISTINO_PRESTAZIONI = {
  visite_principali: {
    title: 'Visite Principali',
    icon: Eye,
    description: 'Seleziona il tipo di visita',
    color: '#2A9FCB',
    services: [
      { 
        name: 'Visita Oculistica Completa', 
        price: 150,
        description: 'Valutazione completa con tutti gli esami inclusi',
        includes: [
          'Motilità oculare',
          'Controllo vista con prescrizione lenti',
          'Esame lampada a fessura',
          'Tonometria',
          'Esame obiettivo',
          'Retinografia',
          'OCT macula e nervo ottico',
          'Ecografia screening'
        ]
      },
      { 
        name: 'Visita di Controllo Glaucoma', 
        price: 100,
        description: 'Controllo specifico per pazienti con glaucoma',
        includes: [
          'Tonometria',
          'Retinografia',
          'Esame lampada a fessura',
          'OCT nervo ottico'
        ]
      },
      { 
        name: 'Visita di Controllo Maculopatia', 
        price: 100,
        description: 'Controllo specifico per pazienti con maculopatia',
        includes: [
          'Misurazione del visus',
          'Retinografia',
          'Esame lampada a fessura'
        ]
      },
    ]
  },
  prestazioni_singole: {
    title: 'Prestazioni Singole (Medico)',
    icon: Stethoscope,
    description: 'Prestazioni eseguibili singolarmente dal medico oculista',
    color: '#3B82F6',
    services: [
      { name: 'Motilità oculare', price: 90 },
      { name: 'Controllo vista con prescrizione lenti', price: 80, note: 'Possibilità di caricare vecchia prescrizione' },
      { name: 'Esame obiettivo alla lampada a fessura', price: 80 },
      { name: 'Tonometria', price: 50 },
      { name: 'Esame obiettivo', price: 100 },
      { name: 'Retinografia', price: 80 },
      { name: 'OCT macula e nervo ottico', price: 100 },
      { name: 'Ecografia screening patologie retiniche e neoplastiche', price: 120 },
      { name: 'Topografia Corneale', price: 80, note: '⚠️ Non indossare lenti a contatto nelle 48-72 ore precedenti' },
    ]
  },
  ortottista: {
    title: 'Prestazioni Ortottista',
    icon: User,
    description: 'Prestazioni eseguite dall\'ortottista',
    color: '#8B5CF6',
    services: [
      { 
        name: 'Visita Ortottica Completa', 
        price: 100,
        description: 'Valutazione ortottica completa',
        includes: [
          'Esame Ortottico (Motilità Oculare per Strabismo)',
          'Misurazione del visus'
        ]
      },
      { name: 'Misurazione del visus', price: 80 },
      { name: 'Campo visivo computerizzato', price: 80, note: 'Solo ortottista' },
    ]
  },
  trattamenti: {
    title: 'Trattamenti Terapeutici (Medico)',
    icon: Zap,
    description: 'Trattamenti terapeutici eseguiti dal medico',
    color: '#F59E0B',
    services: [
      { name: 'LLLT per MGD (Disfunzione Ghiandole di Meibomio)', price: 120 },
      { name: 'Iontoretina per Maculopatia', price: 250 },
    ]
  },
  valutazioni: {
    title: 'Valutazioni Pre-Chirurgiche',
    icon: Scan,
    description: 'Valutazioni incluse nel prezzo dell\'intervento',
    color: '#10B981',
    services: [
      { name: 'Valutazione per Chirurgia Refrattiva', price: 0, note: 'Inclusa nel prezzo dell\'intervento' },
      { name: 'Valutazione per Chirurgia della Cataratta', price: 0, note: 'Inclusa - Intervento €1.500' },
      { name: 'Valutazione per Chirurgia dello Strabismo', price: 0, note: 'Inclusa - Intervento €3.000' },
    ]
  },
  chirurgia: {
    title: 'Chirurgia',
    icon: Syringe,
    description: 'Interventi chirurgici',
    color: '#EF4444',
    services: [
      { name: 'Chirurgia della Cataratta', price: 1500, note: 'Valutazione pre-operatoria inclusa' },
      { name: 'Chirurgia dello Strabismo', price: 3000, note: 'Valutazione pre-operatoria inclusa' },
      { name: 'Rimozione Calazio', price: 480, note: 'Intervento mini-invasivo in anestesia locale' },
      { name: 'Blefaroplastica Superiore', price: 2000, note: 'Chirurgia estetica palpebra superiore' },
      { name: 'Blefaroplastica Inferiore', price: 3000, note: 'Chirurgia estetica palpebra inferiore' },
      { name: 'Rimozione Neoformazione Palpebrale', price: 580, note: 'Asportazione lesioni benigne palpebrali' },
    ]
  }
};

// Componente per mostrare i servizi inclusi
const IncludedServices = ({ includes }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!includes || includes.length === 0) return null;
  
  return (
    <div className="mt-2">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Nascondi dettagli' : `Include ${includes.length} prestazioni`}
      </button>
      {expanded && (
        <ul className="mt-2 pl-4 space-y-1">
          {includes.map((item, idx) => (
            <li key={idx} className="flex items-center gap-2 text-xs text-gray-600">
              <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const ListinoPrezzi = () => {
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#F8F8F8' }}>
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-12 px-4 text-center" style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Listino Prezzi
          </h1>
          <p className="text-lg text-white/90 mb-6" style={{ fontFamily: "'Lora', Georgia, serif" }}>
            Trasparenza e qualità. Scopri tutti i nostri servizi e le relative tariffe.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg text-sm">
            <Check className="w-4 h-4 text-white" />
            <span className="text-white font-medium">Aggiornato Gennaio 2026</span>
          </div>
        </div>
      </section>

      {/* Price List */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Info Box */}
          <div className="mb-12 p-6 rounded-2xl border-2 border-sky-200 bg-sky-50">
            <div className="flex items-start gap-3">
              <Info className="w-6 h-6 text-sky-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-sky-800 mb-2">Come funziona la prenotazione</h3>
                <p className="text-sm text-sky-700">
                  Seleziona la <strong>Visita Oculistica Completa</strong> per una valutazione approfondita con tutti gli esami inclusi, 
                  oppure scegli una <strong>Visita di Controllo</strong> specifica se sei già in follow-up per glaucoma o maculopatia.
                  Le singole prestazioni possono essere aggiunte se necessario.
                </p>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-8">
            {Object.entries(LISTINO_PRESTAZIONI).map(([key, category]) => {
              const Icon = category.icon;
              const isComingSoon = category.comingSoon;
              
              return (
                <div 
                  key={key} 
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden ${isComingSoon ? 'opacity-75' : ''}`}
                >
                  {/* Category Header */}
                  <div 
                    className="p-5 flex items-center gap-4" 
                    style={{ background: category.color }}
                  >
                    <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        {category.title}
                      </h2>
                      {category.description && (
                        <p className="text-sm text-white/80">{category.description}</p>
                      )}
                    </div>
                    {isComingSoon && (
                      <span className="ml-auto px-3 py-1 bg-white/20 rounded-full text-white text-xs font-medium">
                        In arrivo
                      </span>
                    )}
                  </div>

                  {/* Services List */}
                  <div className="divide-y divide-gray-100">
                    {category.services.map((service, index) => (
                      <div 
                        key={index} 
                        className="p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <span className="font-medium text-gray-800">
                              {service.name}
                            </span>
                            {service.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{service.description}</p>
                            )}
                            {service.note && (
                              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                {service.note}
                              </p>
                            )}
                            <IncludedServices includes={service.includes} />
                          </div>
                          <div className="text-right">
                            {service.status === 'coming_soon' ? (
                              <span className="text-sm text-gray-400 italic">Prossimamente</span>
                            ) : service.price === 0 ? (
                              <span className="font-bold text-green-600">Inclusa</span>
                            ) : (
                              <span className="font-bold text-lg" style={{ color: category.color }}>
                                €{service.price}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Note */}
          <div className="mt-12 p-6 rounded-2xl bg-amber-50 border-2 border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Note importanti
            </h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• I prezzi indicati sono riferiti a prestazioni singole</li>
              <li>• La <strong>Visita Oculistica Completa</strong> include tutte le prestazioni diagnostiche di base</li>
              <li>• Le valutazioni pre-chirurgiche sono incluse nel prezzo dell'intervento</li>
              <li>• <strong>Non effettuiamo</strong>: Fluorangiografia, Laser retinici, IVT (in arrivo)</li>
            </ul>
          </div>

          {/* Back Link */}
          <div className="mt-8 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#2A9FCB] hover:underline"
            >
              <ArrowLeft className="w-5 h-5" />
              Torna alla Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ListinoPrezzi;
